import { isStandalone } from './pwa.js';

// The installed app on iOS (owner reports 30/10 and 04/11/2026, the second on OdeParfum: the tab bar floating in the
// middle of the page and the header gone off the top, until the app was closed). iOS can keep the keyboard's height after
// the keyboard is gone: after a field lets go, or coming back from the background. WebKit's visual viewport then stays that
// much shorter than the layout viewport, so scrolling down slides what's on screen inside the layout viewport
// (visualViewport.offsetTop grows up to the stale height), and everything laid out against the layout viewport, fixed and
// sticky, slides up with it. Scrolling up brings it back (bugs.webkit.org 297779: "fixed elements move up and down when the
// scroll direction changes"; Apple forums thread 800125). Nothing makes WebKit measure again: flipping #root's display, the
// first repair (30/10/2026), helped in none of 143 reports from OdeParfum's phones.
//
// So the app follows the offset itself. With no text field focused there is no keyboard, and a healthy viewport has
// offsetTop 0 (nothing to pan into; zoom is off in the installed app), so offsetTop > 0 is exactly how far down the layout
// viewport the screen starts. `--vv-shift` on <html> holds it and `data-vv-shift` says it's on: src/styles/viewport-shift.css
// moves the fixed chrome (translate) and the sticky bars (top) back by it. With a field focused the keyboard's own geometry
// is left alone; a healthy phone never gets the attribute.

export const VV_SHIFT_VAR = '--vv-shift';
export const VV_SHIFT_ATTR = 'data-vv-shift';

/** Re-checks after a field lets go or the app comes back: the keyboard takes a moment to leave, and a late resize can follow. */
export const SHIFT_CHECK_DELAYS_MS = [0, 140, 700];
/** A stale keyboard is reported once it's still there this long after it showed (the closing keyboard is not). */
export const STUCK_REPORT_MS = 700;
/** Short by more than this is a stale keyboard (px): rounding moves the numbers by less. */
export const VIEWPORT_STUCK_PX = 4;

const NOT_TEXT = new Set(['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit']);

const isTextField = (el) => {
  if (!el?.tagName) return false;
  if (el.tagName === 'TEXTAREA' || el.isContentEditable) return true;
  return el.tagName === 'INPUT' && !NOT_TEXT.has(el.type);
};

/** How far down the layout viewport the screen starts (px) when no keyboard can be up; 0 when healthy, zoomed or typing. */
export const viewportShift = (s, typing) => {
  if (typing || s.scale > 1.01) return 0;
  const top = Math.round(s.offsetTop);
  return top >= 1 ? top : 0;
};

/** The keyboard height iOS kept with no keyboard up (px); 0 when fine, zoomed or typing. */
export const staleKeyboard = (s, typing) => {
  if (typing || s.scale > 1.01) return 0;
  const short = s.innerHeight - s.visualHeight;
  return short > VIEWPORT_STUCK_PX ? Math.round(short) : 0;
};

export const viewportStuckMessage = ({ short, shift, sample }) =>
  `viewport stuck: short ${short}px, shift ${shift}px (inner ${Math.round(sample.innerHeight)}, visual ${Math.round(sample.visualHeight)}, top ${Math.round(sample.offsetTop)})`;

// For the sheets (utils/useKeyboardInset, components/auth/useAuthViewport): while the keyboard is stale there is none to
// lift a sheet over. Read live, so a sheet's own viewport listener never sees the answer of a frame before.
let installed = null;

const sampleOf = (win) => ({
  innerHeight: win.innerHeight,
  visualHeight: win.visualViewport.height,
  offsetTop: win.visualViewport.offsetTop,
  scale: win.visualViewport.scale,
});

/** iOS kept a keyboard's height with no keyboard up (the installed app only; false where the shift doesn't run). */
export const viewportStale = () => {
  if (!installed?.visualViewport) return false;
  return staleKeyboard(sampleOf(installed), isTextField(installed.document.activeElement)) > 0;
};

/** Installs the shift once (main.jsx); returns its undo. `onStuck` hears of each stale keyboard: the only evidence from real phones. */
export const installViewportShift = (onStuck = () => undefined, win = typeof window === 'undefined' ? undefined : window) => {
  const vv = win?.visualViewport;
  if (!vv || !isStandalone(win)) return () => undefined;
  const doc = win.document;
  const root = doc.documentElement;
  let written = 0;
  let frame = 0;
  let timers = [];
  let reportTimer = 0;
  let reported = false;

  const typing = () => isTextField(doc.activeElement);

  const write = (shift) => {
    if (shift === written) return;
    written = shift;
    if (shift > 0) {
      root.style.setProperty(VV_SHIFT_VAR, `${shift}px`);
      root.setAttribute(VV_SHIFT_ATTR, '');
    } else {
      root.style.removeProperty(VV_SHIFT_VAR);
      root.removeAttribute(VV_SHIFT_ATTR);
    }
  };

  const report = () => {
    reportTimer = 0;
    const s = sampleOf(win);
    const t = typing();
    const short = staleKeyboard(s, t);
    if (!short || reported) return;
    reported = true;
    onStuck({ short, shift: viewportShift(s, t), sample: s });
  };

  const apply = () => {
    frame = 0;
    const s = sampleOf(win);
    const t = typing();
    write(viewportShift(s, t));
    const short = staleKeyboard(s, t);
    if (!short) {
      // The keyboard went (or a field took it): the next stale keyboard is a new one.
      reported = false;
      if (reportTimer) win.clearTimeout(reportTimer);
      reportTimer = 0;
    } else if (!reported && !reportTimer) {
      reportTimer = win.setTimeout(report, STUCK_REPORT_MS);
    }
  };

  // Scrolling moves the offset every frame: once per frame is enough.
  const onMove = () => {
    if (!frame) frame = win.requestAnimationFrame(apply);
  };
  const settle = () => {
    timers.forEach((t) => win.clearTimeout(t));
    timers = SHIFT_CHECK_DELAYS_MS.map((ms) => win.setTimeout(apply, ms));
  };
  // A field taking focus brings the real keyboard: nothing moves from then on.
  const onFocusIn = () => apply();
  const onVisible = () => {
    if (doc.visibilityState === 'visible') settle();
  };

  installed = win;
  apply();
  doc.addEventListener('focusin', onFocusIn);
  doc.addEventListener('focusout', settle);
  doc.addEventListener('visibilitychange', onVisible);
  win.addEventListener('pageshow', settle);
  win.addEventListener('scroll', onMove, { passive: true });
  win.addEventListener('resize', onMove);
  vv.addEventListener('scroll', onMove);
  vv.addEventListener('resize', onMove);
  return () => {
    timers.forEach((t) => win.clearTimeout(t));
    if (reportTimer) win.clearTimeout(reportTimer);
    if (frame) win.cancelAnimationFrame(frame);
    write(0);
    if (installed === win) installed = null;
    doc.removeEventListener('focusin', onFocusIn);
    doc.removeEventListener('focusout', settle);
    doc.removeEventListener('visibilitychange', onVisible);
    win.removeEventListener('pageshow', settle);
    win.removeEventListener('scroll', onMove);
    win.removeEventListener('resize', onMove);
    vv.removeEventListener('scroll', onMove);
    vv.removeEventListener('resize', onMove);
  };
};
